// Attribution capture — Pass 25.
//
// At form-load time, build the {gclid, fbclid, utm_*, fbp} payload that
// the public lead endpoints persist into All_Leads. The Hub itself does
// NOT write the cookie — the Shopify theme on swisswatch.com.my owns
// the `scw_attr` writer (it captures the click on the marketing site
// before the visitor ever clicks `Contact`). The Hub only reads.
//
// Precedence (highest wins on conflict):
//   1. URL query params (a direct ad-click to a Hub page beats stale
//      Shopify-cookie data — the new click_id is the freshest signal).
//   2. scw_attr cookie (cross-domain memory: visitor landed on
//      swisswatch.com.my from an ad, browsed, then clicked into the
//      Hub. The cookie carries the original click context.)
//   3. _fbp cookie (set by Meta's browser Pixel on either domain).
//
// Cookie shape contract (set by Shopify-side script, NOT this file):
//   scw_attr = encodeURIComponent(JSON.stringify({
//     gclid, fbclid, utm_source, utm_medium, utm_campaign, fbp, ts
//   }))

const COOKIE_NAME = "scw_attr";
const FBP_COOKIE = "_fbp";

function readCookie(name) {
  try {
    const prefix = `${name}=`;
    const cookies = (document.cookie || "").split("; ");
    for (const raw of cookies) {
      if (raw.startsWith(prefix)) {
        return decodeURIComponent(raw.slice(prefix.length));
      }
    }
  } catch {
    /* SSR / restricted-context — ignore */
  }
  return "";
}

function readScwAttrCookie() {
  const raw = readCookie(COOKIE_NAME);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

const FIELDS = ["gclid", "fbclid", "utm_source", "utm_medium", "utm_campaign"];

// On-site links into the Hub (product-page "Enquire Now" CTAs, social
// icons, the top announcement strip) stamp `utm_source=shopify` on the
// URL — not a real traffic source, just a leftover self-referential tag.
// Found 2026-09-09 via the Analytics tab's marketing-source panel: it was
// silently overwriting a visitor's genuine ad-attributed cookie (Google/
// Meta) the instant they clicked through to enquire, because the
// URL-beats-cookie rule below has no way to tell "internal navigation"
// apart from "a real ad landed here." A real ad click carries its own
// gclid/fbclid alongside utm_source; internal navigation never does — so
// that's the signal used to tell them apart, without touching the
// URL-wins precedence for every other case (a genuine direct-to-Hub ad
// click must still be able to override a stale cookie).
const IGNORABLE_URL_SOURCE = "shopify";

// Build the attribution slice. Pure function — pass URLSearchParams in
// for ergonomics with react-router's useSearchParams().
export function buildAttribution(searchParams) {
  const cookie = readScwAttrCookie();
  const getUrl = (key) => (searchParams && searchParams.get && searchParams.get(key)) || "";

  const hasUrlClickId = Boolean(getUrl("gclid") || getUrl("fbclid"));

  const out = {};
  for (const key of FIELDS) {
    let fromUrl = getUrl(key);
    if (key === "utm_source" && fromUrl.trim().toLowerCase() === IGNORABLE_URL_SOURCE && !hasUrlClickId) {
      // Internal-navigation "shopify" tag with no accompanying click ID —
      // not a new touch. Fall through to whatever the cookie already has.
      fromUrl = "";
    }
    out[key] = fromUrl || cookie[key] || "";
  }
  // fbp: URL param wins (unusual but possible), then scw_attr.fbp,
  // then the standalone _fbp cookie set by Meta's browser pixel.
  const urlFbp = getUrl("fbp");
  out.fbp = urlFbp || cookie.fbp || readCookie(FBP_COOKIE) || "";
  return out;
}
